import React, { useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { OrderDto, PlantDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import { Button, Tooltip, vars } from '@trucktrax/trucktrax-common';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import icons from '../icons';
import MarkerLabel from './MarkerLabel';
import TTMarker from './TTMarker';
import styles from '../TTMap.module.css';
import { formatOrderScheduledTime } from '../../../../util/orderUtil';
import { ReduxState } from '../../../../store';
import { ShapeStyles } from '../TTMap';

/** Shared fill opacity for order/jobsite geofence overlays on the live map. */
const GEOFENCE_FILL_OPACITY = 0.25;
const FLAG_ICON_ANCHOR_X = 4.5;
const FLAG_ICON_ANCHOR_Y = 20;
const ORDER_LABEL_VERTICAL_OFFSET = 25;

export default function JobsiteMarker(props: JobsiteMarkerProps) {
  const showJobsites = useSelector((state: ReduxState) => state.mapFilters.showJobsites);
  const {
    order,
    selectedItemId,
    showGeofence = false,
    markerColor = vars.gray700,
    orderDetailsCallback = () => {},
    messageDriversOnOrder = () => {},
    plantList = [],
    ...rest
  }: JobsiteMarkerProps = props;

  const orderNumber = order.orderNumber?.toString();
  const title = orderNumber ? `Order #${orderNumber}` : undefined;

  // Stable label identity — a new JSX element every render forces TTMarker to
  // recreate overlays on every activeMarkers frame.
  const label = useMemo(
    () =>
      title ? (
        <MarkerLabel
          color={markerColor}
          name={title}
          containerStyles={{
            transform: `translate(-50%, calc(-100% - ${ORDER_LABEL_VERTICAL_OFFSET}px))`,
          }}
        />
      ) : undefined,
    [title, markerColor],
  );

  const icon = useMemo(
    () => ({
      path: icons.flagPath,
      fillColor: markerColor,
      fillOpacity: 1,
      strokeWeight: 0,
      anchor: { x: FLAG_ICON_ANCHOR_X, y: FLAG_ICON_ANCHOR_Y },
    }),
    [markerColor],
  );

  const shadowIcon = useMemo(
    () => ({
      ...icon,
      fillColor: 'black',
      fillOpacity: 0.5,
      scale: 1.075,
      strokeWeight: 2,
      strokeColor: 'black',
      strokeOpacity: 0.15,
      anchor: { x: FLAG_ICON_ANCHOR_X + 0.5, y: FLAG_ICON_ANCHOR_Y - 1 },
    }),
    [icon],
  );

  const shapeStyles = useMemo<ShapeStyles>(
    () => ({
      fillColor: markerColor,
      fillOpacity: GEOFENCE_FILL_OPACITY,
      strokeColor: markerColor,
      strokeWeight: 2,
      clickable: false,
      zIndex: 1,
    }),
    [markerColor],
  );

  const { center, radius } = order?.geoZone?.zone?.circle ?? {};

  if (!center?.latitude && !center?.longitude) {
    return null;
  }

  const position = {
    latitude: center?.latitude,
    longitude: center?.longitude,
    lat: center?.latitude ?? 0,
    lng: center?.longitude ?? 0,
  };

  // Built outside the try/catch below on purpose. This order may be the elected
  // geofence owner for a jobsite several orders share, so a throw in the
  // info-pane path must not take the whole site's circle down with it. (DEV-4592)
  const geofenceRenderResult = (
    <Geofence
      position={position}
      radius={radius!}
      shapeStyles={shapeStyles}
      mapRef={props.mapRef}
      showGeofence={showGeofence && showJobsites}
    />
  );

  let markerElement: JSX.Element | null = null;

  try {
    // order may not actually exist in some edge cases
    // should have jobsite location but no order-specific info
    const orderId = order.id?.toString();

    const getPlantName = (plantUrl?: string) => {
      if (!plantUrl) {
        return 'N/A';
      }

      const foundPlant = plantList.find(plant => plant.url === plantUrl);
      return foundPlant ? foundPlant.name : 'N/A';
    };
    const tooltipText = `Plant: ${getPlantName(order?.plant?.url)}`;
    const infoPaneTitle = (
      <div data-test="order-number" title={title}>
        {tooltipText && (
          <Tooltip text={tooltipText}>
            {markerColor && (
              <i
                className={cx(styles.flagIcon, 'icon-flag', 'margin-right-10', 'clickable')}
                style={{ color: markerColor }}
              />
            )}
          </Tooltip>
        )}
        <span className={cx(styles.titleText, 'nowrap-ellipsis')}>{title}</span>
      </div>
    );

    const scheduledDate = formatOrderScheduledTime(order);
    const infoPaneContent = (
      <div className={styles.orderContainer}>
        <div className={cx(styles.flexItem, 'margin-bottom-10')} data-test="infopane-customer-name">
          <b className="margin-right-5 txt-bold">Customer:</b>
          <span className="nowrap-ellipsis" title={order.customerName}>
            {order.customerName}
          </span>
        </div>
        {order.scheduledTime && (
          <div
            className={cx(styles.flexItem, 'margin-bottom-10')}
            data-test="infopane-scheduled-date"
          >
            <b className="margin-right-5 txt-bold">Date:</b>
            <span className="nowrap-ellipsis" title={scheduledDate}>
              {scheduledDate}
            </span>
          </div>
        )}
        <div className={styles.orderFooter}>
          {order.id && (
            <Button
              buttonClassName={cx('tt-btn-basic', styles.orderDetailsBtn)}
              name="View Order Details"
              iconClassName="icon-orders"
              dataTest="view-order-details"
              onClick={orderDetailsCallback}
            />
          )}
          {order.id && (
            <Button
              buttonClassName={cx('tt-btn-basic', styles.orderDetailsBtn)}
              name="Message Drivers on Order"
              iconClassName="icon-messages"
              dataTest="message-drivers-on-order"
              onClick={() => messageDriversOnOrder(order)}
            />
          )}
        </div>
      </div>
    );

    // the pixel offset adjusts the location of the infoPanel around the image
    const pixelOffset: google.maps.Size = {
      width: 9,
      height: -10,
      equals: other => other?.width === 9 && other.height === -10,
    };

    markerElement = (
      <TTMarker
        position={position}
        icon={icon}
        shadowIcon={shadowIcon}
        infoPaneTitle={infoPaneTitle}
        infoPaneContent={infoPaneContent}
        isSelected={!!(orderId && selectedItemId && orderId === selectedItemId)}
        {...rest}
        pixelOffset={pixelOffset}
        label={label}
        isLabelHover={!!label}
        canHover={!!label}
      />
    );
  } catch (e) {
    // this avoids rendering the marker on errors
    // to debug, log this exception
    markerElement = null;
  }

  return (
    <div>
      {markerElement}
      {geofenceRenderResult}
    </div>
  );
}

interface GeofenceProps {
  position: { lat: number; lng: number };
  radius: number;
  mapRef: google.maps.Map;
  shapeStyles: ShapeStyles;
  showGeofence: boolean;
}

const geofencePropsAreEqual = (prevProps: GeofenceProps, nextProps: GeofenceProps) =>
  prevProps.showGeofence === nextProps.showGeofence &&
  prevProps.mapRef === nextProps.mapRef &&
  prevProps.radius === nextProps.radius &&
  prevProps.position.lat === nextProps.position.lat &&
  prevProps.position.lng === nextProps.position.lng &&
  isEqual(prevProps.shapeStyles, nextProps.shapeStyles);

const RenderGeofence = (props: GeofenceProps): JSX.Element => {
  const circleRef = useRef<google.maps.Circle | null>(null);
  const {
    showGeofence,
    mapRef,
    radius,
    position: { lat, lng },
    shapeStyles,
  } = props;

  useEffect(() => {
    circleRef.current?.setMap(null);
    circleRef.current = null;

    if (!showGeofence || !mapRef) {
      return undefined;
    }

    const circleGeozone = new google.maps.Circle({
      center: { lat, lng },
      radius,
      map: mapRef,
      strokeOpacity: 0.8,
      ...shapeStyles,
    });

    circleRef.current = circleGeozone;

    return () => {
      circleGeozone.setMap(null);
      if (circleRef.current === circleGeozone) {
        circleRef.current = null;
      }
    };
  }, [showGeofence, mapRef, lat, lng, radius, shapeStyles]);

  return <></>;
};

/** Exported for unit tests covering circle create/cleanup / no-stack behavior. */
export const Geofence = React.memo(RenderGeofence, geofencePropsAreEqual);

export interface JobsiteMarkerProps {
  order: OrderDto;
  plantList?: PlantDto[];
  selectedItemId?: string;
  showGeofence?: boolean;
  markerColor?: string;
  orderDetailsCallback?: VoidFunc;
  messageDriversOnOrder?: (order: OrderDto) => void;
  mapRef?: any;
  onClick?: VoidFunc;
  onCloseClick?: VoidFunc;
  isSelected?: boolean;
  zIndex?: number;
}
